import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";

export default function ApprovalCard({ update }) {
  const qc = useQueryClient();

  const act = useMutation({
    mutationFn: async (action) => (await api.post(`/stage-updates/${update.id}/${action}`)).data,
    onSuccess: (_d, action) => {
      toast.success(action === "approve" ? "Approved & now live" : "Update rejected");
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["job", update.job_id] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4" data-testid={`approval-card-${update.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <Link to={`/jobs/${update.job_id}`} className="font-mono-tag text-xs text-primary hover:underline">
            {update.job_number}
          </Link>
          <div className="font-heading font-semibold truncate">{update.stage}</div>
          <div className="text-xs text-muted-foreground">Client: {update.client_name}</div>
        </div>
        <StatusBadge status={update.proposed_status} />
      </div>
      {update.custom_label && <p className="text-sm font-medium">{update.custom_label}</p>}
      {update.notes && <p className="text-sm text-muted-foreground">{update.notes}</p>}
      {update.metrics && <p className="text-xs font-mono-tag text-secondary mt-1">metrics: {update.metrics}</p>}
      <p className="text-xs text-muted-foreground mt-2">
        Submitted by {update.submitted_by_name} · {fmtDate(update.submitted_at)}
      </p>
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={() => act.mutate("approve")} disabled={act.isPending}
          data-testid={`approve-button-${update.id}`}>
          <Check className="h-4 w-4 mr-1" /> Approve / Finalise
        </Button>
        <Button size="sm" variant="outline" onClick={() => act.mutate("reject")} disabled={act.isPending}
          data-testid={`reject-button-${update.id}`}>
          <X className="h-4 w-4 mr-1" /> Reject
        </Button>
      </div>
    </div>
  );
}
